import type { Block, BlockLayout } from "../models/ManualAuthoring";
import { allowedAtStart, allowedNext } from "./blockRules";

const TABLE_LAYOUT: BlockLayout = "table";

function withTable(layouts: BlockLayout[]): BlockLayout[] {
  return layouts.includes(TABLE_LAYOUT)
    ? layouts
    : [...layouts, TABLE_LAYOUT];
}

function findPreviousNonTableLayout(
  blocks: Block[],
  fromIndexExclusive: number
): BlockLayout | null {
  for (let i = fromIndexExclusive - 1; i >= 0; i -= 1) {
    const layout = blocks[i].layout;
    if (layout !== TABLE_LAYOUT) return layout;
  }

  return null;
}

/**
 * Returns allowed layouts at a given insert position.
 */
export function getAllowedLayouts(
  blocks: Block[],
  insertIndex: number
): BlockLayout[] {
  // If no blocks exist → allowedAtStart + table
  if (blocks.length === 0) {
    return withTable(allowedAtStart);
  }

  // Clamp index safely
  const safeIndex = Math.max(0, Math.min(insertIndex, blocks.length));

  // If inserting at first position in non-empty list
  if (safeIndex === 0) {
    return withTable(allowedAtStart);
  }

  // Find nearest previous non-table block.
  // Table should not influence what comes next.
  const previousLayout = findPreviousNonTableLayout(blocks, safeIndex);
  if (!previousLayout) {
    return withTable(allowedAtStart);
  }

  return withTable(allowedNext[previousLayout] ?? []);
}

/**
 * Checks if a block can be inserted at index
 */
export function canInsert(
  blocks: Block[],
  layout: BlockLayout,
  insertIndex: number
): boolean {
  const allowed = getAllowedLayouts(blocks, insertIndex);
  return allowed.includes(layout);
}

/**
 * Validates entire block sequence
 */
export function validateStructure(blocks: Block[]): boolean {
  if (blocks.length === 0) return true;

  let previousNonTable: BlockLayout | null = null;

  for (const block of blocks) {
    const current = block.layout;

    if (current === TABLE_LAYOUT) {
      continue;
    }

    if (!previousNonTable) {
      if (!allowedAtStart.includes(current)) {
        return false;
      }
      previousNonTable = current;
      continue;
    }

    const previous = previousNonTable as BlockLayout;
    const allowed: BlockLayout[] = allowedNext[previous] ?? [];
    if (!allowed.includes(current)) {
      return false;
    }

    previousNonTable = current;
  }

  return true;
}

/**
 * Validates whether a move operation is allowed
 */
export function canMove(
  blocks: Block[],
  fromIndex: number,
  toIndex: number
): boolean {
  if (
    fromIndex < 0 ||
    toIndex < 0 ||
    fromIndex >= blocks.length ||
    toIndex >= blocks.length
  ) {
    return false;
  }

  const newBlocks = [...blocks];

  // swap
  [newBlocks[fromIndex], newBlocks[toIndex]] = [
    newBlocks[toIndex],
    newBlocks[fromIndex],
  ];
  return validateStructure(newBlocks);
}
